import JSZip from 'jszip';
import { type Object3D } from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { FILE_NAME, FOLDER_NAME } from './constants';
import SPInspection from './SPInspection';
import { type Inspection, type Manifest } from './types';

class SPProject {
  private _object?: ArrayBuffer;
  private _manifest?: Manifest.Manifest;
  private _model?: Object3D;
  private _inspections: Inspection.Info[] = [];
  private _checkLists: Inspection.Inspection[] = [];

  public get object() { return this._object!; }
  public get manifest() { return this._manifest!; }
  public get model() { return this._model!; }
  public get inspections() { return this._inspections ?? []; }
  public get checkLists() { return this._checkLists ?? []; }

  private constructor(private deserializedSPProjectFile?: JSZip) { }

  public static loadAsync = async (data: string | File | Blob) => {
    try {
      let projectData: File | Blob | null = null;

      if (typeof data === 'string') {
        const response = await fetch(data);
        projectData = await response.blob();
        URL.revokeObjectURL(data);
      } else if (data instanceof File || data instanceof Blob) {
        projectData = data;
      } else {
        throw new Error('Invalid format!');
      }

      const _deserializedSPProjectFile = await new JSZip().loadAsync(projectData);

      const spproject = new SPProject(_deserializedSPProjectFile);
      await spproject.setObjectFile();
      await spproject.setManifest();
      await spproject.setModel();
      await spproject.setCheckLists();
      spproject.setInspectionsFromProject();

      return spproject;

    } catch (error) {
      throw new Error(`Failed to load SPProject: ${error}`);
    }
  };

  private setObjectFile = async (): Promise<void> => {
    const file = this.deserializedSPProjectFile?.file(FILE_NAME.OBJECT);
    if (file == null) throw new Error('Could not find GLB in the project.');

    const arrayBuffer = await file.async('arraybuffer');
    this._object = arrayBuffer;
  };

  private setManifest = async (): Promise<void> => {
    const file = this.deserializedSPProjectFile?.file(FILE_NAME.MANIFEST);
    if (file == null) throw new Error(`Could not find ${FILE_NAME.MANIFEST} in the project.`);

    const JSONString = await file.async('string');
    const manifestData = JSON.parse(JSONString) as Manifest.Manifest;

    this._manifest = manifestData;
  };

  private setModel = async (): Promise<void> => {
    if (this._object == null) throw new Error('Could not find _object.');

    const _gLTFLoader = new GLTFLoader();
    const gltf = await _gLTFLoader.parseAsync(this._object, String());
    this._model = gltf.scene;
  };

  private setCheckLists = async (): Promise<void> => {
    const checkLists = this.manifest.inspections
      ?.filter(inspection => inspection.isTemplate)
      .map(async inspection =>
        await this.getCheckList(inspection.id)
      ) ?? [];

    this._checkLists = await Promise.all(checkLists);
  };

  private setInspectionsFromProject = async (): Promise<void> => {
    this._inspections = this.manifest.inspections
      ?.filter(inspection => !inspection.isTemplate) ?? [];
  };

  private getCheckList = async (checkListId: string): Promise<Inspection.Inspection> => {
    const file =
      this.deserializedSPProjectFile?.file(`${FOLDER_NAME.INSPECTIONS}/${checkListId}/${FILE_NAME.INSPECTION}`);
    if (file == null) throw new Error(`Could not find ${FILE_NAME.INSPECTION} in the inspection.`);

    const JSONString = await file.async('string');
    const inspectionData = JSON.parse(JSONString) as Inspection.Inspection;

    SPInspection.transformInspectionForCompatibility(inspectionData);

    return inspectionData;
  };

  public setInspections = (inspections: Inspection.Info[]): void => {
    this._inspections = inspections;
  };

  public getInspectionById = async (inspectionId: string): Promise<Inspection.Info | undefined> => {
    return this._inspections?.find(inspection => inspection.id == inspectionId);
  };

  public cleanup = (): void => {
    delete this.deserializedSPProjectFile;
    delete this._object;
    delete this._manifest;
    delete this._model;
    this._inspections = [];
    this._checkLists = [];
  };
}

export default SPProject;
